/**
 * Convert raster image to 1-bit monochrome for thermal printer.
 * Barcode Safe Mode: threshold only (no dither) for crisp barcodes.
 * Otherwise optional Floyd-Steinberg dither.
 */

export interface BitmapToMonoOptions {
  /** Use threshold only (no dither) for crisp barcodes/QR. */
  barcodeSafeMode?: boolean;
  /** Threshold 0–255; used when barcodeSafeMode is true. Default 128. */
  threshold?: number;
  /** Margin in pixels (added on each side). Default 0. */
  margin?: number;
}

/**
 * Input: ImageData.data (RGBA), width, height.
 * Output: 1-bit raster, row-major, 8 pixels per byte (MSB first), widthBytes per row.
 */
export function bitmapToMono(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  options: BitmapToMonoOptions = {}
): { raster: Uint8Array; widthBytes: number; height: number } {
  const { barcodeSafeMode = true, threshold = 128, margin = 0 } = options;
  const left = margin;
  const top = margin;
  const w = Math.max(0, width - 2 * margin);
  const h = Math.max(0, height - 2 * margin);
  const widthBytes = Math.ceil(w / 8);
  const raster = new Uint8Array(widthBytes * h);

  for (let y = 0; y < h; y++) {
    const rowOffset = y * widthBytes;
    for (let x = 0; x < w; x++) {
      const sx = left + x;
      const sy = top + y;
      const i = (sy * width + sx) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      const gray = a < 128 ? 255 : Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      const bit = barcodeSafeMode
        ? (gray < threshold ? 1 : 0)
        : (gray < 128 ? 1 : 0);
      const byteIndex = rowOffset + (x >> 3);
      const bitIndex = 7 - (x & 7);
      if (bit) raster[byteIndex] |= 1 << bitIndex;
    }
  }

  return { raster, widthBytes, height: h };
}

/**
 * Draw image from URL (or data URL) to canvas and return ImageData.
 * Used when native returns bitmap as base64.
 */
export function getImageDataFromUrl(url: string): Promise<{ data: Uint8ClampedArray; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2d not available'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      resolve({
        data: imageData.data,
        width: imageData.width,
        height: imageData.height,
      });
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}
